/** The Workbench: sprint board, backlog, and the long-running tracker rail.
 *
 * Trackers sit in their own rail rather than on the board. A 500-machine rollout is not
 * a two-week card, and mixing the two was the pilot's first mistake — the board stopped
 * meaning anything.
 */

import { useMemo, useState } from 'react'
import { UserRound } from 'lucide-react'

import {
  Button,
  Can,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  SectionHeader,
  StatCard,
  inputClass,
} from '@/components/ui'
import { SearchSelect } from '@/components/SearchSelect'
import { usePeople } from '@/features/people/usePeople'
import { useTeams } from '@/features/teams/useTeams'
import { SprintLoad } from '@/features/workbench/SprintLoad'
import { Permissions, useSession } from '@/lib/session'
import { projectOptions } from '@/features/projects/projectOptions'
import { useProjects } from '@/features/projects/useProjects'
import { CompleteSprintDialog } from '@/features/workbench/CompleteSprintDialog'
import { EditSprintDialog } from '@/features/workbench/EditSprintDialog'
import { NewSprintDialog } from '@/features/workbench/NewSprintDialog'
import { EditWorkItemDialog } from '@/features/workbench/EditWorkItemDialog'
import { NewWorkItemDialog } from '@/features/workbench/NewWorkItemDialog'
import { TrackerRail } from '@/features/workbench/TrackerRail'
import { ChangeChip, ChangePicker } from '@/features/workbench/ChangeLink'
import {
  useCompleteSprint,
  useSprintSummary,
  useSprints,
  useStartSprint,
  useUpdateWorkItem,
  useWorkItems,
  type Sprint,
  type WorkItem,
} from '@/features/workbench/useWork'

const COLUMNS = [
  { status: 'todo', label: 'To do' },
  { status: 'in_progress', label: 'In progress' },
  { status: 'blocked', label: 'Blocked' },
  { status: 'done', label: 'Done' },
] as const

const TYPE_TONE = {
  task: 'info',
  issue: 'red',
  change: 'amber',
  tracker: 'neutral',
} as const

function Card({
  item,
  projectName,
  onMove,
  onEdit,
}: {
  item: WorkItem
  projectName: string | null
  onMove: (status: string) => void
  onEdit: () => void
}) {
  return (
    <article
      className={item.isExternal ? 'card p-3' : 'card cursor-grab p-3'}
      // A mirrored card cannot be dragged: its status is the team's own board's to
      // move, and the server would refuse the drop anyway.
      draggable={!item.isExternal}
      onDragStart={(event) => event.dataTransfer.setData('text/plain', item.id)}
    >
      <div className="mb-1 flex items-center gap-2">
        <Pill tone={TYPE_TONE[item.itemType]}>
          {item.isExternal ? (item.externalType ?? item.itemType).toLowerCase() : item.itemType}
        </Pill>
        {item.isExternal ? <Pill>ADO</Pill> : null}
        {item.points !== null ? (
          <span className="micro-label text-fg-muted">{item.points} pts</span>
        ) : null}
        {item.isExternal ? (
          item.externalUrl ? (
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="ml-auto text-xs underline text-fg-muted hover:text-fg"
              title="Managed in Azure DevOps — open it there"
            >
              Open in ADO
            </a>
          ) : null
        ) : (
          <Can permission={Permissions.workEdit}>
            <button
              type="button"
              className="ml-auto text-xs underline text-fg-muted hover:text-fg"
              onClick={onEdit}
            >
              Edit
            </button>
          </Can>
        )}
      </div>
      <p className="text-sm font-medium text-fg">{item.title}</p>
      {/* The project the work belongs to — the card's answer to "why is this here?" */}
      {projectName ? <p className="mt-1 text-xs text-fg-muted">{projectName}</p> : null}
      {item.assigneeName ? (
        <p className="mt-1 text-xs text-fg-muted">{item.assigneeName}</p>
      ) : null}
      {item.referenceUrl ? (
        <a
          href={item.referenceUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-1 block truncate text-xs underline"
          style={{ color: 'var(--status-info)' }}
          onClick={(event) => event.stopPropagation()}
        >
          {item.referenceUrl}
        </a>
      ) : null}
      {/* The linked change, and the way to attach one. The chip shows on any item that has
          a change; the picker only on Change items, so the board does not sprout an
          "attach a change" link on fifty ordinary tasks. */}
      <ChangeChip item={item} />
      {item.itemType === 'change' ? <ChangePicker item={item} /> : null}

      {/* A keyboard path to the same action as the drag. Dragging alone would make the
          board unusable without a mouse. Mirrored items get neither: their status is
          read from Azure DevOps, and a control that cannot work should not render. */}
      {item.isExternal ? null : (
        <label className="mt-2 block">
          <span className="sr-only">Move {item.title} to</span>
          <select
            className="w-full rounded border border-border bg-surface px-2 py-1 text-xs text-fg"
            value={item.status}
            onChange={(event) => onMove(event.target.value)}
          >
            {COLUMNS.map((column) => (
              <option key={column.status} value={column.status}>
                {column.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </article>
  )
}

// "My work" is a standing preference, so it is remembered in this browser. Unavailable
// storage just means the board opens showing everyone's work, as it always did.
const MINE_KEY = 'wpmt.workbench.mine'
// The team lens is remembered too: someone on EDEE wants EDEE's board every time.
const TEAM_KEY = 'wpmt.workbench.team'

function readTeam(): string {
  try {
    return window.localStorage.getItem(TEAM_KEY) ?? ''
  } catch {
    return ''
  }
}

function todayIso(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function readMine(): boolean {
  try {
    return window.localStorage.getItem(MINE_KEY) === '1'
  } catch {
    return false
  }
}

export function WorkbenchPage() {
  const { data: sprintData, isPending, error } = useSprints()
  const [selectedSprint, setSelectedSprint] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [editingItem, setEditingItem] = useState<WorkItem | null>(null)
  const [editingSprint, setEditingSprint] = useState(false)
  const [creatingSprint, setCreatingSprint] = useState(false)
  /**
   * The sprint being closed, and the sprint to start once it is.
   *
   * `thenStart` is set when the user pressed Start on one sprint while another was still
   * open — the dialog closes the old one first, because starting it directly would demote
   * the old sprint to planned and strand it with no way to complete it by clicking.
   */
  const [closing, setClosing] = useState<{ sprint: Sprint; thenStart?: Sprint } | null>(null)
  /** Narrow the board to one project's work — '' means every project. */
  const [projectFilter, setProjectFilter] = useState('')
  /** Which columns the board shows: everything, only what's done, or only what isn't. */
  const [focus, setFocus] = useState<'done' | 'outstanding' | null>(null)
  /** A personal board: only the items assigned to the signed-in person. */
  const [mine, setMine] = useState(readMine)
  const { data: session } = useSession()
  /** One team's sprints and work — '' is every team. */
  const [team, setTeamState] = useState(readTeam)
  /** One person's work — '' is everyone. "My work only" is the same filter set to you. */
  const [person, setPerson] = useState('')
  const { data: teamData } = useTeams()
  const { data: peopleData } = usePeople()
  const teams = (teamData?.teams ?? []).filter((t) => t.team !== 'unassigned')
  const setTeam = (next: string) => {
    setTeamState(next)
    setSelectedSprint(null)
    try {
      window.localStorage.setItem(TEAM_KEY, next)
    } catch {
      // Still filters for this visit.
    }
  }
  const toggleMine = () => {
    const next = !mine
    setMine(next)
    try {
      window.localStorage.setItem(MINE_KEY, next ? '1' : '0')
    } catch {
      // The toggle still works for this visit.
    }
  }

  const startSprint = useStartSprint()
  const completeSprint = useCompleteSprint()
  const updateItem = useUpdateWorkItem()

  const allSprints = sprintData?.sprints ?? []
  // A team's board offers its own sprints, unassigned and mirrored ones, and any sprint
  // holding its work — EDEE had 27 items in IT Ops' Sprint 5 the day sprints got teams.
  const sprints = team
    ? allSprints.filter((s) => s.team === team || s.team === null || s.itemTeams.includes(team))
    : allSprints
  const activeId = team
    ? (sprintData?.activeByTeam[team] ?? null)
    : (sprintData?.activeSprintId ?? null)
  // With nothing active, open on the sprint whose dates include today — the team's own
  // first, then the busiest. "The newest" opened EDEE's Sprint 35 for IT Ops on 2026-10-07
  // while their current Sprint 5 had simply never been started.
  const today = todayIso()
  const runningToday = sprints
    .filter((s) => !s.isExternal && s.startDate <= today && s.endDate >= today && s.status !== 'completed')
    .sort(
      (a, b) =>
        Number(b.team === team) - Number(a.team === team) || b.itemCount - a.itemCount,
    )[0]
  const selectedVisible =
    selectedSprint === 'backlog' || sprints.some((s) => s.id === selectedSprint) ? selectedSprint : null
  const currentId = selectedVisible ?? activeId ?? runningToday?.id ?? sprints[0]?.id ?? null
  const current = sprints.find((s) => s.id === currentId) ?? null

  // The sprint that would be superseded by starting another: the active one *of the same
  // team* — each team has its own. Mirrored sprints follow their own calendar.
  const openSprintFor = (sprint: Sprint) =>
    allSprints.find(
      (s) => s.status === 'active' && !s.isExternal && (s.team ?? null) === (sprint.team ?? null),
    ) ?? null

  /**
   * Start a sprint, closing whatever is still open first.
   *
   * The old behaviour — start it and let the server demote the previous one — is what
   * confused Brian on 2026-09-03: Sprint 2 silently became `planned`, and the Complete
   * button only shows for an active sprint, so it could not be closed at all.
   */
  const requestStart = (sprint: Sprint) => {
    const openSprint = openSprintFor(sprint)
    if (openSprint && openSprint.id !== sprint.id) {
      setClosing({ sprint: openSprint, thenStart: sprint })
      return
    }
    startSprint.mutate(sprint.id)
  }

  const isBacklog = currentId === 'backlog'
  const { data: itemData } = useWorkItems({
    ...(isBacklog ? { backlog: true } : { sprintId: currentId ?? undefined }),
    projectId: projectFilter || undefined,
  })
  // With "My work only" on, the headline numbers are counted from your items alone, by
  // the server's own rollup — so the numbers and the board below them always agree.
  const personId = mine ? session?.userId : person || undefined
  const { data: summary } = useSprintSummary(
    isBacklog ? null : currentId,
    personId,
    team || undefined,
  )
  const { data: projectData } = useProjects({})
  // Memoised: `?? []` builds a new array each render and would defeat the memo below.
  const projects = useMemo(() => projectData?.projects ?? [], [projectData])
  const projectNames = useMemo(
    () => new Map(projects.map((project) => [project.id, project.name])),
    [projects],
  )

  // Memoised: `?? []` builds a new array each render, so the column grouping below would
  // recompute on every keystroke elsewhere on the page.
  //
  // "Mine" keys on the assignee link, not the name, for the same reason the picker exists:
  // a typed name is nobody as far as a filter is concerned.
  const allItems = useMemo(() => itemData?.items ?? [], [itemData])
  const projectTeams = useMemo(
    () => new Map(projects.map((project) => [project.id, project.team])),
    [projects],
  )
  // The team lens works through each item's project: an item with no project belongs to no
  // team and shows only on the all-teams board.
  const teamItems = useMemo(
    () =>
      team
        ? allItems.filter((item) => item.projectId && projectTeams.get(item.projectId) === team)
        : allItems,
    [allItems, team, projectTeams],
  )
  const items = useMemo(
    () => (personId ? teamItems.filter((item) => item.assigneeUserId === personId) : teamItems),
    [teamItems, personId],
  )
  const visibleAssignees = useMemo(
    () => new Set(items.map((item) => item.assigneeUserId).filter((id): id is string => Boolean(id))),
    [items],
  )
  /**
   * Which columns the board renders.
   *
   * Filtering to a single column would leave three empty boxes looking broken, so
   * "outstanding" keeps all three unfinished columns and only "done" narrows to one.
   */
  const visibleColumns = useMemo(() => {
    if (focus === 'done') return COLUMNS.filter((column) => column.status === 'done')
    if (focus === 'outstanding') return COLUMNS.filter((column) => column.status !== 'done')
    return COLUMNS
  }, [focus])

  const byStatus = useMemo(
    () =>
      Object.fromEntries(
        COLUMNS.map((column) => [column.status, items.filter((i) => i.status === column.status)]),
      ) as Record<string, WorkItem[]>,
    [items],
  )

  const move = (item: WorkItem, status: string) => {
    if (item.status === status) return
    // Mirrored items have no move control, but a drop from a stale render could still
    // land here — refuse locally rather than bounce off the server.
    if (item.isExternal) return
    updateItem.mutate({ id: item.id, status, row_version: item.rowVersion })
  }

  if (isPending) return <Loading label="Loading the workbench" />
  if (error) return <ErrorState error={error} />

  return (
    <div className="mx-auto max-w-[1500px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Workbench</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Two-week sprints for scheduled work; a separate rail for long-running trackers.
          Closing work updates its project automatically.
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2" role="group" aria-label="Team">
        {[{ team: '', shortLabel: 'All teams' }, ...teams].map((t) => (
          <button
            key={t.team || 'all'}
            type="button"
            aria-pressed={team === t.team}
            onClick={() => setTeam(t.team)}
            className="rounded-pill border px-3 py-1 text-sm font-semibold transition"
            style={
              team === t.team
                ? { backgroundColor: 'var(--brand)', color: 'var(--brand-on)', borderColor: 'var(--brand)' }
                : { borderColor: 'var(--border)', color: 'var(--fg)' }
            }
          >
            {t.shortLabel}
          </button>
        ))}
      </div>

      <div className="card mb-4 flex flex-wrap items-end justify-between gap-3 p-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Board">
            <select
              className={inputClass}
              value={currentId ?? ''}
              onChange={(event) => setSelectedSprint(event.target.value)}
            >
              {sprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name}
                  {!team && sprint.team ? ` · ${teams.find((t) => t.team === sprint.team)?.shortLabel ?? ''}` : ''}
                  {sprint.status === 'active' ? ' — active' : ''}
                  {sprint.status === 'completed' ? ' — completed' : ''}
                </option>
              ))}
              <option value="backlog">Backlog</option>
            </select>
          </Field>
          <div className="w-64">
            <Field label="Project">
              <SearchSelect
                value={projectFilter}
                onChange={setProjectFilter}
                options={projectOptions(projects, session?.userId)}
                emptyLabel="All projects"
              />
            </Field>
          </div>
          <div className="w-56">
            <Field label="Person">
              <SearchSelect
                value={mine ? (session?.userId ?? '') : person}
                onChange={(value) => {
                  setPerson(value)
                  if (mine) toggleMine()
                }}
                options={(peopleData ?? []).map((p) => ({ value: p.id, label: p.displayName }))}
                emptyLabel="Everyone"
              />
            </Field>
          </div>
          <button
            type="button"
            aria-pressed={mine}
            onClick={() => {
              setPerson('')
              toggleMine()
            }}
            title="Show only the work assigned to you"
            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition ${
              mine ? 'bg-brand text-brand-on hover:brightness-95' : 'border border-border text-fg hover:bg-canvas'
            }`}
          >
            <UserRound size={14} aria-hidden />
            My work only
          </button>
          {current && !isBacklog ? (
            <div className="pb-1 text-xs text-fg-muted">
              {current.startDate} → {current.endDate}
              {current.status === 'active' ? ` · ${current.daysRemaining} days left` : ''}
            </div>
          ) : null}
        </div>

        <Can permission={Permissions.workManageSprints}>
          <div className="flex items-center gap-2">
            {/* A mirrored sprint's name, dates and lifecycle follow its Azure DevOps
                iteration; the buttons that would edit it here do not render. */}
            {current?.isExternal ? (
              <span className="text-xs text-fg-muted">Sprint mirrored from Azure DevOps</span>
            ) : null}
            <Button onClick={() => setCreatingSprint(true)}>New sprint</Button>
            {current && !isBacklog && !current.isExternal ? (
              <Button onClick={() => setEditingSprint(true)}>Edit sprint</Button>
            ) : null}
            {current &&
            !current.isExternal &&
            current.status !== 'active' &&
            current.status !== 'completed' ? (
              <Button variant="primary" onClick={() => requestStart(current)}>
                Start sprint
              </Button>
            ) : null}
            {/* Offered for a planned sprint as well as an active one. A sprint that
                finished while the next was started sits in `planned`, and refusing to
                close it here is what left Sprint 2 stranded. */}
            {current && !current.isExternal && current.status !== 'completed' ? (
              <Button onClick={() => setClosing({ sprint: current })}>Complete sprint</Button>
            ) : null}
          </div>
        </Can>
      </div>

      {completeSprint.data ? (
        <div className="card mb-4 p-3 text-sm" style={{ borderLeft: '3px solid var(--brand)' }}>
          Sprint completed: {completeSprint.data.completedCount} done
          {/* Named destinations. A bare count reads as work having quietly gone
              somewhere, which is the thing this whole change is about. */}
          {completeSprint.data.carriedTo
            ? `, ${completeSprint.data.carriedOverCount} carried into ${completeSprint.data.carriedTo.name}`
            : ''}
          {completeSprint.data.sentToBacklogCount
            ? `, ${completeSprint.data.sentToBacklogCount} returned to the backlog`
            : ''}
          {!completeSprint.data.carriedOverCount ? ', nothing left open' : ''}.
        </div>
      ) : null}

      {summary && !isBacklog ? (
        <>
          <div className="mb-2 flex flex-wrap gap-3">
            {/* Pressing a number narrows the board to the items behind it. With a handful
                of items the four columns already show everything; with forty, "just show
                me what is left" is the whole question. */}
            <StatCard
              label="Items"
              value={summary.itemCount}
              tone="info"
              hint="Show every column"
              active={focus === null}
              onClick={() => setFocus(null)}
            />
            <StatCard
              label="Points done"
              value={`${summary.donePoints} / ${summary.committedPoints}`}
              tone="green"
              hint="Show only what is finished"
              active={focus === 'done'}
              onClick={() => setFocus(focus === 'done' ? null : 'done')}
            />
            <StatCard
              label="Complete"
              value={`${summary.percentComplete}%`}
              tone="amber"
              hint="Show what is still outstanding"
              active={focus === 'outstanding'}
              onClick={() => setFocus(focus === 'outstanding' ? null : 'outstanding')}
            />
          </div>
          <p className="mb-6 text-xs text-fg-muted">
            Press a number to narrow the board. Press it again to show every column.
          </p>
        </>
      ) : null}

      {!isBacklog ? <SprintLoad items={allItems} visibleAssignees={visibleAssignees} /> : null}

      <SectionHeader
        action={
          <Can permission={Permissions.workEdit}>
            <Button variant="primary" onClick={() => setCreating(true)}>
              New work item
            </Button>
          </Can>
        }
      >
        {isBacklog ? 'Backlog' : (current?.name ?? 'Board')}
      </SectionHeader>

      {updateItem.error ? (
        <div className="mb-4">
          <ErrorState error={updateItem.error} />
        </div>
      ) : null}

      {mine && allItems.length > 0 ? (
        <p className="mb-3 text-xs text-fg-muted">
          Showing the {items.length} of {allItems.length} items on this board assigned to you.
          The numbers above and the long-running work below are yours alone too.
        </p>
      ) : null}

      {items.length === 0 ? (
        mine && allItems.length > 0 ? (
          <EmptyState title="Nothing on this board is assigned to you">
            Turn off “My work only” to see everyone&apos;s work.
          </EmptyState>
        ) : (
          <EmptyState title="Nothing here yet">
            Add a work item, or pick another board.
          </EmptyState>
        )
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {visibleColumns.map((column) => (
            <section
              key={column.status}
              className="rounded-lg p-2"
              style={{ backgroundColor: 'color-mix(in srgb, var(--fg-muted) 6%, transparent)' }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault()
                const id = event.dataTransfer.getData('text/plain')
                const item = items.find((i) => i.id === id)
                if (item) move(item, column.status)
              }}
            >
              <h3 className="micro-label mb-2 flex justify-between px-1 text-fg-muted">
                <span>{column.label}</span>
                <span>{byStatus[column.status]?.length ?? 0}</span>
              </h3>
              <div className="space-y-2">
                {(byStatus[column.status] ?? []).map((item) => (
                  <Card
                    key={item.id}
                    item={item}
                    projectName={item.projectId ? (projectNames.get(item.projectId) ?? null) : null}
                    onMove={(status) => move(item, status)}
                    onEdit={() => setEditingItem(item)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="mt-10">
        <TrackerRail assigneeUserId={personId} />
      </div>

      {creating ? (
        <NewWorkItemDialog
          sprintId={isBacklog ? null : currentId}
          projectId={projectFilter}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {editingItem ? (
        <EditWorkItemDialog item={editingItem} onClose={() => setEditingItem(null)} />
      ) : null}

      {creatingSprint ? (
        <NewSprintDialog
          sprints={allSprints}
          team={team}
          teams={teams}
          onClose={() => setCreatingSprint(false)}
          // Show the sprint just made. Leaving the board where it was is how three
          // clicks produced three sprints nobody saw.
          onCreated={(sprint) => setSelectedSprint(sprint.id)}
        />
      ) : null}

      {editingSprint && current ? (
        <EditSprintDialog sprint={current} onClose={() => setEditingSprint(false)} />
      ) : null}

      {closing ? (
        <CompleteSprintDialog
          sprint={closing.sprint}
          sprints={allSprints}
          thenStart={closing.thenStart}
          onClose={() => setClosing(null)}
        />
      ) : null}
    </div>
  )
}
